import { useState } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Landmark, TrendingUp, TrendingDown, Clock, ArrowLeftRight, Banknote } from "lucide-react";

const fmt = (n) => `£${Number(n ?? 0).toFixed(2)}`;

const TYPE_META = {
  bond_contribution: { label: "Contribution", icon: TrendingUp, cls: "text-emerald-700" },
  bond_cancellation_deduction: { label: "Cancellation deduction", icon: TrendingDown, cls: "text-red-600" },
  bond_cohort_matured: { label: "Cohort matured", icon: Clock, cls: "text-blue-600" },
  bond_negative_balance_offset: { label: "Negative-balance offset", icon: ArrowLeftRight, cls: "text-amber-600" },
  bond_payout_declared: { label: "Payout declared", icon: Banknote, cls: "text-emerald-700" },
  bond_adjustment: { label: "Adjustment", icon: ArrowLeftRight, cls: "text-slate-600" },
};

export default function BondAccountView() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [stale, setStale] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiClient.get("/bond/account");
      setData(res.data);
      setStale(false);
    } catch (e) {
      // Package 6H — honest error state: a failed request is NEVER shown as a
      // genuine £0 balance or an empty ledger.
      setLoadError(e?.response?.data?.detail || e?.message || "Unable to load your bond account");
      if (data) setStale(true);
    } finally {
      setLoading(false);
    }
  };

  const ledger = data?.ledger || [];
  const contributions = ledger.filter((l) => l.type === "bond_contribution");
  const deductions = ledger.filter((l) => l.type === "bond_cancellation_deduction");
  const matured = ledger.filter((l) => l.type === "bond_cohort_matured");
  const offsets = ledger.filter((l) => l.type === "bond_negative_balance_offset");
  const payouts = ledger.filter((l) => l.type === "bond_payout_declared");
  const upcoming = [...new Set(contributions.map((c) => c.maturityMonth).filter(Boolean))]
    .filter((m) => !matured.some((x) => x.maturityMonth === m))
    .sort();
  const negative = Number(data?.balance ?? 0) < 0;

  return (
    <Card className="border-slate-200 shadow-none" data-testid="bond-account-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Landmark className="h-5 w-5 text-amber-600" />
            <h3 className="font-heading font-bold text-slate-900">My Bond Account</h3>
          </div>
          <Button variant="outline" size="sm" data-testid="toggle-bond-account-btn"
            onClick={() => { const n = !open; setOpen(n); if (n && !data) load(); }}>
            {open ? "Hide" : "View"}
          </Button>
        </div>

        {open && (loading ? (
          <div className="text-center py-6 text-slate-400">Loading…</div>
        ) : loadError && !data ? (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-center" data-testid="bond-account-error">
            <p className="text-sm font-medium text-red-700">Couldn't load your bond account</p>
            <p className="text-xs text-red-500 mt-1">{String(loadError)}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={load} data-testid="bond-account-retry-btn">Retry</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {loadError && stale && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700" data-testid="bond-account-stale-banner">
                Live refresh failed — showing the last confirmed figures, which may be out of date.
                <Button variant="ghost" size="sm" className="h-6 px-2 ml-2 text-amber-700" onClick={load} data-testid="bond-account-stale-retry-btn">Retry</Button>
              </div>
            )}
            <div className={`rounded-lg p-4 ${negative ? "bg-red-50" : "bg-amber-50/60"}`} data-testid="bond-balance-box">
              <p className="text-xs text-slate-500">Current bond balance</p>
              <p className={`text-2xl font-bold ${negative ? "text-red-600" : "text-slate-900"}`} data-testid="bond-balance-value">
                {fmt(data?.balance)}
              </p>
              {negative && <p className="text-xs text-red-500 mt-1">Negative balance carries forward and is reduced by future contributions and matured releases before any payout.</p>}
            </div>

            {ledger.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4" data-testid="bond-empty">No bond activity yet.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Stat label="Contributions" value={contributions.length} sub={fmt(contributions.reduce((s, x) => s + Number(x.amount || 0), 0))} testid="bond-stat-contributions" />
                  <Stat label="Deductions" value={deductions.length} sub={fmt(Math.abs(deductions.reduce((s, x) => s + Number(x.amount || 0), 0)))} testid="bond-stat-deductions" />
                  <Stat label="Matured cohorts" value={matured.length} testid="bond-stat-matured" />
                  <Stat label="Offsets" value={offsets.length} testid="bond-stat-offsets" />
                  <Stat label="Payouts" value={payouts.length} sub={fmt(Math.abs(payouts.reduce((s, x) => s + Number(x.amount || 0), 0)))} testid="bond-stat-payouts" />
                  <Stat label="Upcoming maturities" value={upcoming.length} sub={upcoming[0] || "—"} testid="bond-stat-upcoming" />
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="bond-ledger-table">
                    <thead>
                      <tr className="border-b text-left text-slate-500">
                        <th className="pb-2 pr-3">Date</th>
                        <th className="pb-2 pr-3">Type</th>
                        <th className="pb-2 pr-3">Cohort</th>
                        <th className="pb-2 pr-3 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.map((l) => {
                        const meta = TYPE_META[l.type] || { label: l.type, icon: ArrowLeftRight, cls: "text-slate-600" };
                        const Icon = meta.icon;
                        return (
                          <tr key={l.id} className="border-b border-slate-100" data-testid={`bond-ledger-row-${l.id}`}>
                            <td className="py-2 pr-3 text-slate-500">{String(l.timestamp || "").slice(0, 10)}</td>
                            <td className="py-2 pr-3">
                              <span className={`inline-flex items-center gap-1 ${meta.cls}`}><Icon className="h-3.5 w-3.5" />{meta.label}</span>
                            </td>
                            <td className="py-2 pr-3 text-slate-500">{l.cohortMonth || "—"}</td>
                            <td className={`py-2 pr-3 text-right font-medium ${Number(l.amount) < 0 ? "text-red-600" : "text-slate-800"}`}>
                              {Number(l.amount) === 0 ? "—" : (Number(l.amount) > 0 ? "+" : "") + fmt(l.amount)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3" data-testid={testid}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-lg font-bold text-slate-900">{value}</p>
      {sub != null && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  );
}
